import type {
  QuoteItemSection,
  QuoteItemView,
  QuoteTemplateOption,
  RenderableItem,
  UpdateableGroup
} from '@paintscout/util/builder'
import {
  addItemsToGroup,
  copyItems,
  deleteGroups,
  findItem,
  findItems,
  getFilesForItem,
  getGroup,
  getItemSection,
  getItems,
  getJobAddress,
  getJobIdentifier,
  getObjectLabels,
  getQuoteLoanApplication,
  getQuoteOptions,
  moveItems,
  removeItems,
  reorderItems,
  setEstimator,
  setJobAddress,
  setJobIdentifier,
  setQuoteOptions,
  updateFilesForItem
} from '@paintscout/util/builder'
import { isQuoteEditable } from '@paintscout/util/util'
import { AlertDialog, ConfirmationDialog } from '@paintscout/ui'
import { withClientOptions, withDialogStackContext, withUser } from '@paintscout/ui-business'
import type { WithClientOptions, WithDialogStackContext, WithUser } from '@paintscout/ui-business'
import concat from 'lodash/concat'
import uniq from 'lodash/uniq'
import type { Auth0User, QuoteDocument, QuoteFile } from 'paintscout'
import React from 'react'
import type { Contact } from '@paintscout/api'
import { findNextKey, findPreviousKey, hasCustomTotal, isLooselyEqual } from '@paintscout/util'
import EditAreaDialog from '../dialogs/EditAreaDialog'

import EditGroupDialog from '../dialogs/EditGroupDialog'
import EditItemFilesDialog from '../dialogs/EditItemFilesDialog'
import EditLineItemDialog from '../dialogs/EditLineItemDialog'
import EditQuoteDetailsDialog from '../dialogs/EditQuoteDetailsDialog'
import EditSubstrateDialog from '../dialogs/EditSubstrateDialog'

import type { QuoteView } from '..'
import ConvertItemsDialog from '../dialogs/ConvertItemsDialog'
import EditBulkDialog from '../dialogs/EditBulkDialog'
import EditTermsDialog from '../dialogs/EditTermsDialog'
import ItemConfigurationDialog from '../dialogs/ItemConfigurationDialog'
import JobAddressDialog from '../dialogs/JobAddressDialog'
import type { WisetackPromoResponse } from '../Wisetack'
import type { QuoteContextValue } from './QuoteContext'
import type { QuoteContextProviderProps } from './QuoteContextProvider'
import QuoteContextProvider from './QuoteContextProvider'

const SUBSTRATES_HAVE = 'substrates have'
const SUBSTRATE_HAS = 'substrate has'

export interface EditableQuoteContextProviderProps
  extends QuoteContextProviderProps,
    WithDialogStackContext,
    WithClientOptions,
    WithUser {
  isDirty?: boolean
  isEditable?: boolean
  isNoteEditable?: boolean
  canDeletePayment?: boolean
  hasActiveAccount?: boolean
  canAddAdditionalWork?: boolean
  isSaving?: boolean
  quoteTab?: QuoteView

  editingTemplate?: QuoteTemplateOption

  wisetackPromo?: WisetackPromoResponse

  onSave?: (args: { quote: QuoteDocument }) => any
  onSync?: () => any
  onReset?: () => any
  onChange?: (quote: QuoteDocument, blockDirty?: boolean) => any

  // contact
  onSelectContact?: () => Contact | Promise<Contact>
  onEditContact?: () => Contact | Promise<Contact>
  onCreateContact?: () => Contact | Promise<Contact>
  onEditSecondaryContact?: () => Contact | Promise<Contact>
  onCreateSecondaryContact?: () => Contact | Promise<Contact>
  onSelectSecondaryContact?: () => Contact | Promise<Contact>
  onRemoveSecondaryContact?: () => Contact | Promise<Contact>

  onAddItem?: (section?: QuoteItemSection, insertAfterKey?: string, type?: string) => any

  onEditDetails?: () => void
  onEditNote?: () => void

  onAccept?: (pending?: boolean) => any

  onPresentationPageChange?: (presentationPage: string) => any
  onSidebarPageChange?: (sidebarPage: string) => any
  onEditInfo?: () => any
}

interface EditableQuoteContextProviderState {
  value: QuoteContextValue
}

class EditableQuoteContextProvider extends React.Component<
  EditableQuoteContextProviderProps,
  EditableQuoteContextProviderState
> {
  constructor(props: EditableQuoteContextProviderProps) {
    super(props)

    this.state = {
      value: {
        cleanQuote: props.quote,
        quote: props.quote,
        tableView: props.quote.defaultView as 'area' | 'substrate',
        presentationPage: props.presentationPage,
        sidebarPage: props.sidebarPage,
        estimator: props.estimator,
        isEditable: isQuoteEditable(props.quote),
        isNoteEditable: props.isNoteEditable,
        canDeletePayment: props.canDeletePayment,
        hasActiveAccount: props.hasActiveAccount,
        canAddAdditionalWork: props.canAddAdditionalWork,
        isDirty: props.isDirty,
        isSaving: props.isSaving,
        wisetackPromo: props.wisetackPromo,
        // handlers
        ...getHandlersFromProps(this.props),
        onEditTerms: this.handleEditTerms,
        onAddItem: props.onAddItem ?? this.handleAddItem,
        onEditItem: this.handleEditItem,
        onItemAction: this.handleItemAction,
        onTableViewChange: this.handleTableViewChange,
        onEditJobAddress: this.handleEditJobAddress,
        // onEditDetails: this.handleEditDetails,
        save: this.handleSaveQuote,
        sync: this.handleSyncQuote,
        reset: this.handleReset,
        onReorder: this.handleReorder,
        onEditItemFiles: this.handleEditItemFiles,
        toggleEditable: this.toggleEditable,
        updateQuote: this.updateQuote,
        selectedItemKeys: [],
        setSelectedItemKeys: this.setSelectedItemKeys
      }
    }
  }

  public setSelectedItemKeys = (keys: string[]) => {
    this.setState((state) => ({
      value: {
        ...state.value,
        selectedItemKeys: keys
      }
    }))
  }

  public static getDerivedStateFromProps(
    props: EditableQuoteContextProviderProps,
    state: EditableQuoteContextProviderState
  ): EditableQuoteContextProviderState {
    const {
      isDirty,
      isSaving,
      quote,
      estimator,
      isEditable,
      isNoteEditable,
      canDeletePayment,
      hasActiveAccount,
      canAddAdditionalWork,
      wisetackPromo
    } = props

    const loanApplication = getQuoteLoanApplication({ quote, contact: quote.contact })
    if (loanApplication && !!quote.contact) {
      quote.contact.wisetackLoanApplication = loanApplication
    }

    return {
      value: {
        ...state.value,
        ...getHandlersFromProps(props),
        tableView: quote.defaultView === 'area' || quote.defaultView === 'substrate' ? quote.defaultView : 'area',
        presentationPage: '',
        sidebarPage: 'index',
        estimator,
        cleanQuote: !isDirty ? quote : state.value.cleanQuote,
        quote,
        isDirty,
        isSaving,
        isEditable,
        isNoteEditable,
        canDeletePayment,
        hasActiveAccount,
        canAddAdditionalWork,
        wisetackPromo
      }
    }
  }

  public handleEditDetails = () => {
    const { quote } = this.props
    const { openDialog, dismissDialog } = this.props.dialogStackContext

    openDialog(EditQuoteDetailsDialog, {
      quote,
      onConfirm: (newQuote: QuoteDocument) => {
        this.updateQuote({ quote: newQuote })
        dismissDialog()
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  public handleEditJobAddress = () => {
    const { quote } = this.props
    const { openDialog, dismissDialog } = this.props.dialogStackContext

    openDialog(JobAddressDialog, {
      jobAddress: getJobAddress({ quote }),
      jobIdentifier: getJobIdentifier({ quote }),
      onConfirm: ({ identifier, ...jobAddress }) => {
        const withNewAddress = setJobAddress({ quote, jobAddress })
        const withIdentifier = setJobIdentifier({ quote: withNewAddress, identifier })

        this.updateQuote({
          quote: withIdentifier
        })

        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  public handleEditTerms = () => {
    const { quote, isEditable } = this.props
    const { options } = this.props.clientOptions
    const objectLabels = getObjectLabels({ options })
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    return openDialog(EditTermsDialog, {
      editable: isEditable,
      quote,
      objectLabels,
      onConfirm: (newQuote: QuoteDocument) => {
        this.updateQuote({ quote: newQuote })
        dismissDialog()
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  public getItemKeys = (
    quote: QuoteDocument,
    view: 'area' | 'substrate',
    section: QuoteItemSection,
    showLineItems: boolean
  ) => {
    const { options } = this.props.clientOptions
    return getItems({ quote, options, view, section, consumer: 'customer', showLineItems }).map((item) => item.key)
  }

  public getWorkOrderItemKeys = (quote: QuoteDocument) => {
    const { options } = this.props.clientOptions
    const { tableView } = this.state.value
    const { showWorkOrderAreas, showWorkOrderSubstrates, showWorkOrderAreaOptions } = getQuoteOptions({
      quote,
      options
    })

    let itemKeys: string[] = []
    if (showWorkOrderAreas) {
      itemKeys = this.getItemKeys(quote, 'area', 'bid', true)
    }
    if (showWorkOrderSubstrates) {
      itemKeys = concat(
        itemKeys,
        this.getItemKeys(quote, 'substrate', 'bid', !(showWorkOrderSubstrates && !showWorkOrderAreas))
      )
    }
    if (showWorkOrderAreaOptions) {
      itemKeys = concat(itemKeys, this.getItemKeys(quote, tableView, 'options', true))
    }

    return uniq(itemKeys)
  }

  public handleEditItem = (
    key: string,
    localQuote?: QuoteDocument,
    section?: QuoteItemSection,
    navigatedDirty?: boolean
  ) => {
    const quote = localQuote || this.props.quote
    const { options } = this.props.clientOptions
    const { quoteTab } = this.props
    const { tableView } = this.state.value
    const { openDialog, dismissDialog, dismissAllDialogs } = this.props.dialogStackContext

    const item = findItem({ quote, options, key, section })
    const consumer = quoteTab === 'work-order' ? 'crew' : 'customer'
    if (!item) return

    const itemKeys =
      quoteTab === 'work-order'
        ? this.getWorkOrderItemKeys(quote)
        : this.getItemKeys(quote, tableView, item?.section ?? getItemSection(item), true)

    const nextKey = findNextKey(key, itemKeys)
    const prevKey = findPreviousKey(key, itemKeys)

    let EditDialog: any
    if (item.type === 'area') {
      EditDialog = EditAreaDialog
    } else if (item.type === 'substrate') {
      EditDialog = EditSubstrateDialog
    } else if (item.type === 'group') {
      EditDialog = EditGroupDialog
    } else {
      EditDialog = EditLineItemDialog
    }

    return openDialog(EditDialog, {
      quote,
      item,
      section,
      maxWidth: 'lg',
      fullWidth: true,
      onConfirm: (newQuote: QuoteDocument, navItemKey?: string) => {
        const isEqual = isLooselyEqual(quote, newQuote)
        const navigatingDirty = navigatedDirty || !isEqual
        const isAlreadyDirty = this.state.value.isDirty

        const shouldAutoAssign = options.options?.corporate?.autoAssignEstimators && !newQuote.owner?.id
        const estimatorProps = shouldAutoAssign
          ? {
              quote: newQuote,
              user: this.props.user.user as Auth0User,
              signature: this.props.user.preferences.signature
            }
          : undefined
        const maybeAssignedQuote = estimatorProps ? setEstimator(estimatorProps) : newQuote

        if (navItemKey) {
          this.props.dialogStackContext.stack = []
          this.handleEditItem(navItemKey, maybeAssignedQuote, section, navigatingDirty)
        } else {
          this.updateQuote({
            quote: maybeAssignedQuote,
            autosave: false,
            blockDirty: isEqual && !navigatedDirty && !isAlreadyDirty
          })
          dismissAllDialogs()
        }
      },
      onCancel: (quote: QuoteDocument, isDirty?: boolean) => {
        const isDirtyVal = typeof isDirty !== 'undefined' ? isDirty : !isLooselyEqual(quote, this.state.value.quote)

        if (isDirtyVal) {
          dismissDialog()
          openDialog(ConfirmationDialog, {
            message: 'Unsaved changes will be lost',
            onConfirm: () => {
              // Need to use the given quote group not the one from the context for group changes
              this.updateQuote({
                quote: {
                  ...(item.type === 'group' ? quote : this.state.value.quote),
                  groups: {
                    ...quote.groups
                  }
                },
                blockDirty: !this.state.value.isDirty
              })
              dismissAllDialogs()
            },
            onCancel: () => {
              dismissDialog()
            }
          })
        } else {
          this.updateQuote({ quote: this.state.value.quote, blockDirty: !this.state.value.isDirty })
          dismissAllDialogs()
        }
      },
      nextItemKey: nextKey,
      previousItemKey: prevKey,
      consumer
    } as React.ComponentProps<typeof EditDialog>)
  }

  public handleEditBulk = (keys: string[]) => {
    const { quote } = this.props
    const { openDialog, dismissDialog } = this.props.dialogStackContext

    return openDialog(EditBulkDialog, {
      quote,
      keys,
      onConfirm: (newQuote: QuoteDocument) => {
        const shouldAutoAssign =
          this.props.clientOptions.options.options?.corporate?.autoAssignEstimators && !newQuote.owner?.id
        const estimatorProps = shouldAutoAssign
          ? {
              quote: newQuote,
              user: this.props.user.user as Auth0User,
              signature: this.props.user.preferences.signature
            }
          : undefined
        const maybeAssignedQuote = estimatorProps ? setEstimator(estimatorProps) : newQuote

        this.updateQuote({ quote: maybeAssignedQuote })
        dismissDialog()
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  public handleAddItem = (section?: QuoteItemSection, insertAfterKey?: string, type?: string) => {
    const { quote, editingTemplate } = this.props
    const { openDialog, dismissDialog } = this.props.dialogStackContext

    const { options } = this.props.clientOptions

    return openDialog(ItemConfigurationDialog, {
      quote,
      type,
      quoteTypes: editingTemplate?.quoteTypes,
      item: null,
      insertAfterKey,
      section,
      onConfirm: (newQuote: QuoteDocument) => {
        if (options.options?.corporate?.autoAssignEstimators && !newQuote.owner?.id) {
          const estimatorProps = {
            quote: newQuote,
            user: this.props.user.user as Auth0User,
            signature: this.props.user.preferences.signature
          }
          this.updateQuote({ quote: setEstimator(estimatorProps) })
        } else {
          this.updateQuote({ quote: newQuote })
        }
        dismissDialog()
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  public handleItemAction = (
    action:
      | 'copy'
      | 'delete'
      | 'edit-text'
      | 'edit-bulk'
      | 'make-option'
      | 'archive'
      | 'unarchive'
      | 'move-to-bid'
      | 'group'
      | 'ungroup'
      | 'move-to-additional'
      | 'move-to-pending'
      | 'move-to-added-options'
      | 'addToGroup'
      | 'convert'
      | 'edit',
    section: QuoteItemSection,
    keys: string[]
  ) => {
    switch (action) {
      case 'copy':
        this.handleCopyItems(keys, section)
        break
      case 'delete':
        this.handleDeleteItems(keys, section)
        break
      case 'edit-bulk':
        this.handleEditBulk(keys)
        break
      case 'make-option': {
        this.handleMoveItems({ keys, previousSection: section, section: 'options' })
        break
      }
      case 'archive': {
        this.handleMoveItems({ keys, previousSection: section, section: 'archived' })
        break
      }
      case 'unarchive': {
        this.handleMoveItems({ keys, previousSection: section, section: 'bid' })
        break
      }
      case 'group': {
        this.handleGroupItems({ keys, section })
        break
      }
      case 'addToGroup': {
        this.handleAddItemsToGroup({ keys })
        break
      }
      case 'ungroup': {
        this.handleUngroupItems({ keys })
        break
      }
      case 'move-to-bid': {
        this.handleMoveItems({ keys, previousSection: section, section: 'bid' })
        break
      }
      case 'move-to-additional': {
        this.handleMoveItems({ keys, previousSection: section, section: 'additional' })
        break
      }
      case 'move-to-pending': {
        this.handleMoveItems({ keys, previousSection: section, section: 'pending' })
        break
      }
      case 'move-to-added-options': {
        this.handleMoveItems({ keys, previousSection: section, section: 'added-options' })
        break
      }
      case 'convert': {
        this.handleConvertItems({ keys, section })
        break
      }
      case 'edit': {
        this.handleEditItem(keys[0], null, section)
      }
    }
  }

  public handleGroupItems = (args: { keys: string[]; section: QuoteItemSection }) => {
    const { quote } = this.props
    const { openDialog, dismissDialog } = this.props.dialogStackContext

    const groupedChildren = Object.values(quote?.groups ?? {}).reduce((acc, group) => [...acc, ...group.children], [])
    const alreadyGroupedSubstrates = args.keys.filter((key) => groupedChildren.includes(key) && quote.substrates[key])
    if (alreadyGroupedSubstrates.length && quote.version < 5) {
      const groupedSubstratesString = alreadyGroupedSubstrates.map((key) => quote.substrates[key].label).join(', ')
      const multiple = alreadyGroupedSubstrates.length > 1
      return openDialog(AlertDialog, {
        message:
          `The following ${multiple ? SUBSTRATES_HAVE : SUBSTRATE_HAS} already been grouped: ` +
          groupedSubstratesString +
          `.\nPlease ungroup ${multiple ? 'them' : 'it'} before grouping ${multiple ? 'them' : 'it'} again.`,
        onConfirm: dismissDialog
      })
    } else {
      return openDialog(EditGroupDialog, {
        quote,
        keys: args.keys,
        section: args.section,
        onConfirm: (newQuote: QuoteDocument) => {
          this.updateQuote({ quote: newQuote })
          dismissDialog()
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    }
  }

  public handleAddItemsToGroup = (args: { keys: string[] }) => {
    const { quote } = this.props
    const { options } = this.props.clientOptions
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    const { keys } = args
    const items = findItems({ quote, keys })
    const [group] = items.filter((item) => item.type === 'group') as UpdateableGroup[]
    const groupedChildren = Object.values(quote?.groups ?? {}).reduce((acc, group) => [...acc, ...group.children], [])
    const alreadyGroupedSubstrates = keys.filter((key) => groupedChildren.includes(key) && quote.substrates[key])
    if (alreadyGroupedSubstrates.length && quote.version < 5) {
      const groupedSubstratesString = alreadyGroupedSubstrates.map((key) => quote.substrates[key].label).join(', ')
      const multiple = alreadyGroupedSubstrates.length > 1
      return openDialog(AlertDialog, {
        message:
          `The following ${multiple ? SUBSTRATES_HAVE : SUBSTRATE_HAS} already been grouped: ` +
          groupedSubstratesString +
          `.\nPlease ungroup ${multiple ? 'them' : 'it'} before grouping ${multiple ? 'them' : 'it'} again.`,
        onConfirm: dismissDialog
      })
    } else {
      if (group) {
        const addedItemKeys = items.map((item) => item.key).filter((key) => key !== group.key)
        const updatedQuote = addItemsToGroup({ keys: addedItemKeys, options, group, quote })
        const updatedGroup = getGroup({ key: group.key, quote: updatedQuote })

        return openDialog(EditGroupDialog, {
          quote: updatedQuote,
          keys: updatedGroup.children,
          item: updatedGroup,
          addItems: true,
          onConfirm: (newQuote: QuoteDocument) => {
            if (newQuote) {
              this.updateQuote({ quote: newQuote })
            }

            dismissDialog()
          },
          onCancel: () => {
            dismissDialog()
          }
        })
      }
    }
  }

  public handleConvertItems = (args: { keys: string[]; section?: QuoteItemSection }) => {
    const { keys, section } = args
    const { quote } = this.props
    const { tableView } = this.state.value
    const { openDialog, dismissAllDialogs } = this.props.dialogStackContext
    return openDialog(ConvertItemsDialog, {
      quote,
      itemKeys: keys,
      view: tableView,
      section,
      onConfirm: (newQuote: QuoteDocument) => {
        this.updateQuote({ quote: newQuote })
        dismissAllDialogs()
      },
      onCancel: dismissAllDialogs
    })
  }

  public handleUngroupItems = (args: { keys: string[] }) => {
    const { keys } = args
    const { quote } = this.props
    const { options } = this.props.clientOptions
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    return openDialog(ConfirmationDialog, {
      message: 'Are you sure you want to ungroup these items?',
      onConfirm: () => {
        const updatedQuote = deleteGroups({ quote, options, keys })

        this.updateQuote({ quote: updatedQuote })
        dismissDialog()
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  public handleMoveItems = (args: { keys: string[]; previousSection: QuoteItemSection; section: QuoteItemSection }) => {
    const { quote } = this.props
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    const { options } = this.props.clientOptions
    const { keys, section, previousSection } = args

    const movingGroups = keys.filter((key) => quote.groups?.[key])
    const movingGroupChildren = movingGroups.reduce((acc, key) => {
      const group = quote.groups[key]
      return [...acc, ...group.children]
    }, [])
    const duplicateDestinationChildren = Object.values(quote.groups ?? {}).reduce((acc, group) => {
      if (group.section === section || (section === 'pending' && group.section === 'additional')) {
        const alreadyGroupedChildren = group.children.reduce((acc, child) => {
          if (movingGroupChildren.includes(child)) {
            const childLabel = quote.substrates[child]?.label
            return [...acc, childLabel]
          } else {
            return acc
          }
        }, [])
        return [...acc, ...alreadyGroupedChildren]
      } else {
        return acc
      }
    }, [])
    if (duplicateDestinationChildren.length && quote.version < 5) {
      const groupedSubstratesString = duplicateDestinationChildren.join(', ')
      const multiple = duplicateDestinationChildren.length > 1
      const groupString =
        section === 'pending' ? 'Pending/Additional' : section[0].toLocaleUpperCase() + section.slice(1)
      return openDialog(AlertDialog, {
        messages: [
          `The following ${
            multiple ? SUBSTRATES_HAVE : SUBSTRATE_HAS
          } already been grouped in ${groupString} section: ${groupedSubstratesString}.`,
          `Must ungroup before proceeding.`
        ],
        onConfirm: dismissDialog,
        title: 'Duplicate Grouped Substrates'
      })
    } else {
      const updatedQuote = moveItems({ quote, options, keys, section, previousSection })
      this.updateQuote({ quote: updatedQuote })
    }
  }

  public handleCopyItems = (keys: string[], section: QuoteItemSection) => {
    const { quote } = this.props
    const { options } = this.props.clientOptions
    this.updateQuote({ quote: copyItems({ quote, options, keys, section }) })
  }

  public handleDeleteItems = (keys: string[], section: QuoteItemSection) => {
    const { options } = this.props.clientOptions
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    // Remove back button for archived items
    const isArchived = section === 'archived'
    return openDialog(ConfirmationDialog, {
      message: `Are you sure you want to delete th${keys.length === 1 ? 'is item' : 'ese items'}?`,
      onConfirm: () => {
        const quote = removeItems({ quote: this.props.quote, options, keys, section })

        this.updateQuote({ quote })
        dismissDialog()
      },
      onCancel: () => dismissDialog(),
      hideBackButton: isArchived
    })
  }

  public handleReorder = (args: {
    items: RenderableItem[]
    section: QuoteItemSection
    view: QuoteItemView
    addIndex?: number
  }) => {
    const { items, section, view, addIndex } = args

    const quote = reorderItems({ quote: this.props.quote, view, section, items })
    const quoteOptions = getQuoteOptions({ quote })
    quoteOptions.addIndex = {
      ...quoteOptions.addIndex,
      [section]: addIndex
    }
    const updatedQuote = setQuoteOptions({ quoteOptions, quote })

    if (updatedQuote) {
      this.updateQuote({ quote: updatedQuote })
    }
  }

  public handleTableViewChange = (view: string) => {
    const v = view as 'area' | 'substrate'
    const { state } = this
    if (view === state.value.tableView) {
      return
    }

    this.updateQuote({
      quote: {
        ...this.props.quote,
        defaultView: v
      }
    })
  }

  public toggleEditable = (editable?: boolean) => {
    if (typeof editable !== 'undefined') {
      this.setState((prevState) => ({
        value: {
          ...prevState.value,
          isEditable: editable
        }
      }))
    } else {
      this.setState((prevState) => ({
        value: {
          ...prevState.value,
          isEditable: !prevState.value.isEditable
        }
      }))
    }
  }

  public handleEditItemFiles = (args: { itemKey: string; index?: number }) => {
    const { itemKey, index } = args
    const { options } = this.props.clientOptions
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    const files = getFilesForItem({ quote: this.props.quote, options, itemKey })
    openDialog(EditItemFilesDialog, {
      files,
      index,
      onDelete: (_key, currentFiles) => {
        this.updateQuote({
          quote: updateFilesForItem({ quote: this.props.quote, options, itemKey: itemKey, files: currentFiles })
        })
      },
      onConfirm: (newFiles: QuoteFile[]) => {
        this.updateQuote({
          quote: updateFilesForItem({ quote: this.props.quote, options, files: newFiles, itemKey })
        })
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  /**
   * Updates the quote in the state
   * also sets the updated date to Date.now()
   * also sets the version to 2 (in case it isn't already)
   * also sets the tableView to area if totals are overriden
   */
  public updateQuote = (args: { quote: QuoteDocument; autosave?: boolean; blockDirty?: boolean }) => {
    const { quote, autosave, blockDirty } = args
    const updatedQuote = {
      ...quote,
      dates: {
        ...quote.dates,
        ...(!blockDirty && !autosave ? { updated: Date.now() } : {})
      },
      defaultView: hasCustomTotal(quote.areas) ? 'area' : quote.defaultView
    }

    if (this.props.onChange) {
      this.props.onChange(updatedQuote, blockDirty)
    }
    if (autosave) {
      this.props.onSave({ quote: updatedQuote })
    }
  }

  public handleSaveQuote = async (quote?: QuoteDocument) => {
    if (this.props.onSave) {
      return this.props.onSave({ quote: quote || this.props.quote })
    }
  }

  public handleSyncQuote = async () => {
    if (this.props.onSync) {
      return this.props.onSync()
    }
  }

  public handleReset = () => {
    if (this.props.onReset) {
      this.props.onReset()
    }
  }

  public render() {
    return (
      <QuoteContextProvider {...this.props} contextValue={this.state.value}>
        {this.props.children}
      </QuoteContextProvider>
    )
  }
}

// helper function to get context onWhatever callbacks that are passed on to EditableQuoteContextProvider props
function getHandlersFromProps(props: EditableQuoteContextProviderProps) {
  return {
    onSelectContact: props.onSelectContact,
    onCreateContact: props.onCreateContact,
    onEditContact: props.onEditContact,
    onEditSecondaryContact: props.onEditSecondaryContact,
    onCreateSecondaryContact: props.onCreateSecondaryContact,
    onSelectSecondaryContact: props.onSelectSecondaryContact,
    onRemoveSecondaryContact: props.onRemoveSecondaryContact,
    onEditDetails: props.onEditDetails,
    onEditNote: props.onEditNote,
    onSidebarPageChange: props.onSidebarPageChange,
    onEditInfo: props.onEditInfo,
    onPresentationPageChange: props.onPresentationPageChange,
    onAccept: props.onAccept
  }
}

export default withClientOptions(withDialogStackContext(withUser(EditableQuoteContextProvider)))
